import { useEffect, useState } from 'react';
import EediSquiggle from '@/assets/images/EediSquiggle';
import styled from 'styled-components';
import { getImageUrl } from '@/config/settings';
import { Loading } from '@/components/common';

const DefaultStyledImageContainer = styled.div`
  svg,
  img {
    width: 100%;
    height: auto;
  }
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'auto')};
`;

const LoadingImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  background-color: var(--gray-lightest);
  aspect-ratio: 1 / 0.75;
  padding: 2rem;
`;

/**
 * @desc - Returns the imageUrl parsed against the environment variables.
 * @param string $src - The src = image url to be parsed.
 * @param string $alt - The alt tag text string used in the image element.
 * @param object $styledImageContainer - A 'styled-components' derrived container used
 * to wrap the image.
 * @param bool $loadingImage - If true the 'EediSquiggle' image is used.
 * If false the 'Spinner' is used.
 * @param function $onClick - Associated onClick function. Remember to bind this if you need to.
 * @return JSX.Element
 */
const Image = ({
  src = null,
  alt = 'Image Loading...',
  styledImageContainer = DefaultStyledImageContainer,
  loadingImage = true,
  loadingAnimation = true,
  onClick = undefined,
  placeholder = null,
  onLoad,
  ...props
}) => {
  const StyledImageContainer = styledImageContainer;
  const [isLoaded, setIsLoaded] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState(null);

  useEffect(() => {
    const imageUrl = getImageUrl(src);

    if (!imageUrl) {
      setResolvedSrc(null);
      setIsLoaded(false);
      return;
    }
    let cancelled = false;

    setIsLoaded(false);
    const img = new window.Image();
    img.src = imageUrl;
    img.onload = () => {
      if (cancelled) return;
      setResolvedSrc(imageUrl);
      setIsLoaded(true);
    };
    img.onerror = () => {
      if (cancelled) return;
      setResolvedSrc(imageUrl);
      setIsLoaded(true);
    };

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [src]);

  return (
    <StyledImageContainer onClick={onClick} {...props}>
      {!isLoaded ? (
        placeholder ? (
          <img src={placeholder} alt={alt} aria-busy="true" />
        ) : loadingImage ? (
          <LoadingImage>
            <EediSquiggle />
          </LoadingImage>
        ) : (
          loadingAnimation && <Loading />
        )
      ) : (
        <img
          src={resolvedSrc}
          alt={alt}
          onLoad={onLoad}
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      )}
    </StyledImageContainer>
  );
};

export default Image;
